if (!customElements.get('collection-tabs-custom')) {

  class CollectionTabsCustom extends HTMLElement {

    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      this.tabs = Array.from(this.querySelectorAll('.collection-tabs_tab'));
      this.grid = this.querySelector('[id^="product-grid-custom-"]');

      if (!this.grid || !this.tabs.length) return;

      this.sectionId = this.grid.dataset.sectionId;
      this.requests = new Map();
      this.currentTab = null;
      this.observer = null;

      this.viewLinks = Array.from(
        this.querySelectorAll('.section-heading__actions a, .js-view-collection-link')
      );

      this.tabs.forEach(tab => {
        const button = tab.querySelector('button');
        if (!button) return;

        button.addEventListener('click', e => {
          e.preventDefault();
          this.activate(tab);
        });

        // Pré-carrega a aba antes do clique
        ['pointerenter', 'focus'].forEach(evt => {
          button.addEventListener(evt, () => this.prefetch(tab), { passive: true });
        });
      });

      // 1ª aba já veio renderizada no HTML (SSR): não precisa de fetch
      const ssrPanel = this.grid.querySelector('.collection-tabs_panel.active');

      if (ssrPanel) {
        this.setActiveTab(this.tabs[0]);
        this.grid.classList.remove('is-pending');
        return;
      }

      this.loadWhenVisible();
    }

    disconnectedCallback() {
      this.stopObserver();
    }

    /* ---------- Carregamento da 1ª aba (sem SSR) ---------- */

    loadWhenVisible() {
      const first = this.tabs[0];

      if (!('IntersectionObserver' in window)) {
        this.activate(first);
        return;
      }

      this.observer = new IntersectionObserver(entries => {
        if (!entries.some(entry => entry.isIntersecting)) return;

        this.stopObserver();

        // Se o usuário já escolheu uma aba, não sobrescreve
        if (!this.currentTab) this.activate(first);
      }, { rootMargin: '400px 0px' });

      this.observer.observe(this);
    }

    stopObserver() {
      if (this.observer) {
        this.observer.disconnect();
        this.observer = null;
      }
    }

    /* ---------- Helpers ---------- */

    targetId(tab) {
      const button = tab.querySelector('button');
      return `collection-custom-${this.sectionId}-${button.dataset.panelId}`;
    }

    findPanel(tab) {
      return this.grid.querySelector(`[id="${this.targetId(tab)}"]`);
    }

    setActiveTab(tab) {
      this.currentTab = tab;

      this.tabs.forEach(t => {
        const isActive = t === tab;
        const button = t.querySelector('button');

        t.classList.toggle('active', isActive);
        t.setAttribute('aria-selected', String(isActive));
        if (button) button.setAttribute('aria-selected', String(isActive));
      });

      const url = tab.querySelector('button').dataset.collectionUrl;

      if (url) {
        this.viewLinks.forEach(link => { link.href = url; });
      }
    }

    showPanel(panel) {
      this.grid.querySelectorAll('.collection-tabs_panel').forEach(p => {
        p.classList.remove('active');
        p.setAttribute('aria-hidden', 'true');
      });

      panel.classList.add('active');
      panel.removeAttribute('aria-hidden');
      this.grid.classList.remove('is-pending');
    }

    /* ---------- Fetch ---------- */

    fetchPanel(tab) {
      const targetId = this.targetId(tab);

      if (this.requests.has(targetId)) return this.requests.get(targetId);

      const button = tab.querySelector('button');
      const url = `${button.dataset.collectionUrl}?section_id=${this.sectionId}`;

      const request = fetch(url)
        .then(response => {
          if (!response.ok) throw new Error(`HTTP ${response.status}`);
          return response.text();
        })
        .then(html => {
          const doc = new DOMParser().parseFromString(html, 'text/html');

          const panel =
            doc.querySelector(`[id="product-grid-custom-${this.sectionId}"] .collection-tabs_panel`) ||
            doc.querySelector('.collection-tabs_panel');

          if (!panel) throw new Error('Painel não encontrado na resposta');

          panel.id = targetId;
          panel.classList.remove('active');
          panel.setAttribute('aria-hidden', 'true');

          this.grid.appendChild(panel);

          return panel;
        })
        .catch(error => {
          // Permite tentar de novo
          this.requests.delete(targetId);
          throw error;
        });

      this.requests.set(targetId, request);

      return request;
    }

    prefetch(tab) {
      if (this.findPanel(tab) || this.requests.has(this.targetId(tab))) return;

      this.fetchPanel(tab).catch(() => {});
    }

    /* ---------- Ativação de aba ---------- */

    async activate(tab) {
      this.stopObserver();
      this.setActiveTab(tab);
      this.clearError();

      let panel = this.findPanel(tab);

      if (!panel) {
        this.grid.classList.add('is-loading');

        try {
          panel = await this.fetchPanel(tab);
        } catch (error) {
          console.error('Falha ao carregar a coleção:', error);

          if (this.currentTab === tab) this.showError(tab);
        } finally {
          this.grid.classList.remove('is-loading');
        }
      }

      // O usuário trocou de aba enquanto carregava
      if (!panel || this.currentTab !== tab) return;

      this.showPanel(panel);
    }

    /* ---------- Erro ---------- */

    showError(tab) {
      this.grid.classList.remove('is-pending');

      const box = document.createElement('div');
      box.className = 'collection-tabs_error';
      box.setAttribute('role', 'alert');

      const text = document.createElement('p');
      text.textContent = 'Não foi possível carregar os produtos.';

      const retry = document.createElement('button');
      retry.type = 'button';
      retry.textContent = 'Tentar novamente';
      retry.addEventListener('click', () => this.activate(tab));

      box.append(text, retry);
      this.grid.appendChild(box);
    }

    clearError() {
      this.grid.querySelectorAll('.collection-tabs_error').forEach(el => el.remove());
    }
  }

  customElements.define('collection-tabs-custom', CollectionTabsCustom);
}